<template>
  <div class="relative isolate">
    <component
      class="relative z-10"
      :class="[
        cls,
        rotate && 'transform',
        rotate === '90'
          ? 'rotate-90'
          : rotate === '180'
            ? 'rotate-180'
            : rotate === '-90'
              ? '-rotate-90'
              : '',
      ]"
      :is="iconComp"
    />
    <component
      v-if="blobId && !app.brandingEnabled"
      class="blob absolute inset-0 z-0"
      :class="[blobCls, blobColor]"
      :is="blobComp"
    />
  </div>
</template>

<script lang="ts">
export type Size = '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const classMap: Record<Size, string> = {
  '3xs': 'w-2 h-2',
  '2xs': 'w-3 h-3',
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-10 h-10',
  '2xl': 'w-12 h-12',
}
const blobClassMap: Record<Size, string> = {
  '3xs': 'w-3 h-3',
  '2xs': 'w-4 h-4',
  xs: 'w-5 h-5',
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
  xl: 'w-12 h-12',
  '2xl': 'w-14 h-14',
}
</script>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import { useAppState } from '/@/App/state'
import iconMap from './svgs'

export type BlobId = 'blob1' | 'blob2' | 'blob3' | 'blob4'
const {
  size = 'sm',
  blobColor = 'text-pink-medium',
  blob,
  icon,
  iconId: iconIdProp,
  rotate,
} = defineProps<{
  icon?: keyof typeof iconMap | `${keyof typeof iconMap}:${BlobId}`
  iconId?: keyof typeof iconMap
  size?: Size
  blob?: BlobId
  blobColor?: string
  rotate?: '90' | '180' | '-90'
}>()

const app = useAppState()
const iconId = computed(() => {
  if (iconIdProp) return iconIdProp
  return (icon?.split(':')?.[0] as keyof typeof iconMap) ?? ('x' as const)
})
const blobId = computed(() => {
  if (blob) return blob
  return icon?.split(':')?.[1]
})
const iconComp = computed(() => defineAsyncComponent(iconMap[iconId.value]))
const cls = computed(() => classMap[size])
const blobComp = computed(() =>
  defineAsyncComponent(
    iconMap[`blobs/${blobId.value}` as keyof typeof iconMap],
  ),
)
const blobCls = computed(() => blobClassMap[size])
</script>

<style scoped lang="scss">
.blob {
  transform: translate(-10%, -10%);
}
</style>
